// Moteur de calcul du magnétisme : 100 % de fonctions pures, aucune dépendance
// au DOM, au store, à React ni au temps. C'est le seul endroit où vit la règle
// d'alignement ; tout le reste (session, overlay, haptique) n'est que de la
// plomberie autour.
//
// Toutes les coordonnées sont en pixels « document » (non zoomés), repère local
// de la colonne/du masque : { x, y, width, height }, x/y = coin haut-gauche.

import { SNAP_CONFIG } from './snapConfig';

export const GUIDE_KIND = { EDGE: 'edge', CENTER: 'center' };

// Tolérance de comparaison de flottants : deux ancrages séparés de moins de
// EPS sont considérés alignés (les rects DOM ne tombent jamais rond).
const EPS = 0.01;

// Les trois ancrages d'une bbox sur un axe, dans l'ordre : bord bas, centre,
// bord haut. L'ordre est figé, ANCHOR_KINDS s'indexe dessus.
const anchorsOnX = ({ x, width }) => [x, x + width / 2, x + width];
const anchorsOnY = ({ y, height }) => [y, y + height / 2, y + height];
const ANCHOR_KINDS = [GUIDE_KIND.EDGE, GUIDE_KIND.CENTER, GUIDE_KIND.EDGE];

// Axe X → trait vertical, dont l'étendue se mesure sur Y (et inversement).
const AXIS = {
  x: { anchors: 'ax', span: 'y', size: 'height' },
  y: { anchors: 'ay', span: 'x', size: 'width' },
};

// Pré-calcul des ancrages des cibles : fait UNE fois au début du drag, pas à
// chaque mousemove (cf. snapSession). C'est ce qui garde la boucle de calcul à
// quelques dizaines de soustractions par frame.
export const prepareSnapTargets = (bboxes) =>
  bboxes.map((bbox) => ({ ...bbox, ax: anchorsOnX(bbox), ay: anchorsOnY(bbox) }));

const withAnchors = (target) =>
  target.ax && target.ay ? target : { ...target, ax: anchorsOnX(target), ay: anchorsOnY(target) };

// Meilleur accrochage sur un axe : on teste les 3×3 combinaisons d'ancrages
// (bord/centre de la box déplacée contre bord/centre de la cible), on garde la
// plus proche. À égalité stricte, l'alignement de centres gagne : c'est le plus
// lisible visuellement, et sans ce départage un centrage parfait serait volé
// par un alignement de bords fortuit à la même distance.
//
// `anchorIndices` : ancrages de la box qui peuvent s'accrocher. Tous pour un
// déplacement ; au redimensionnement, seul le bord tiré bouge (cf.
// computeResizeSnap).
const ALL_ANCHORS = [0, 1, 2];

const bestAlignmentOnAxis = ({
  draggedAnchors, targets, anchorsKey, threshold, anchorIndices = ALL_ANCHORS,
}) => {
  let best = null;
  for (const target of targets) {
    const targetAnchors = target[anchorsKey];
    for (const i of anchorIndices) {
      for (let j = 0; j < 3; j += 1) {
        const delta = targetAnchors[j] - draggedAnchors[i];
        const distance = delta < 0 ? -delta : delta;
        if (distance > threshold) continue;
        const isCenter =
          ANCHOR_KINDS[i] === GUIDE_KIND.CENTER && ANCHOR_KINDS[j] === GUIDE_KIND.CENTER;
        if (
          !best ||
          distance < best.distance - EPS ||
          (distance < best.distance + EPS && isCenter && !best.isCenter)
        ) {
          best = { distance, delta, position: targetAnchors[j], isCenter };
        }
      }
    }
  }
  return best;
};

// Traits à afficher une fois la position accrochée connue : toute coïncidence
// exacte d'ancrages devient un guide, pas seulement celle qui a provoqué le
// snap (une box peut être alignée à gauche sur une cible ET centrée sur une
// autre). Les guides sont dédoublonnés par position, et leur étendue est
// l'union des box concernées — un trait qui court du haut de la plus haute au
// bas de la plus basse, comme dans Figma/Illustrator.
const guidesOnAxis = ({
  axis, draggedBBox, targets, maxGuides, overhang, anchorIndices = ALL_ANCHORS,
}) => {
  const { anchors: anchorsKey, span: spanKey, size: sizeKey } = AXIS[axis];
  const draggedAnchors = axis === 'x' ? anchorsOnX(draggedBBox) : anchorsOnY(draggedBBox);
  const guides = [];
  for (const target of targets) {
    const targetAnchors = target[anchorsKey];
    for (const i of anchorIndices) {
      for (let j = 0; j < 3; j += 1) {
        const gap = targetAnchors[j] - draggedAnchors[i];
        if (gap > EPS || gap < -EPS) continue;
        const position = targetAnchors[j];
        const start = Math.min(draggedBBox[spanKey], target[spanKey]) - overhang;
        const end =
          Math.max(
            draggedBBox[spanKey] + draggedBBox[sizeKey],
            target[spanKey] + target[sizeKey]
          ) + overhang;
        const existing = guides.find((guide) => Math.abs(guide.position - position) <= EPS);
        if (existing) {
          existing.start = Math.min(existing.start, start);
          existing.end = Math.max(existing.end, end);
          existing.targetIds.push(target.id);
          continue;
        }
        if (guides.length >= maxGuides) continue;
        guides.push({
          axis,
          position,
          start,
          end,
          kind:
            ANCHOR_KINDS[i] === GUIDE_KIND.CENTER && ANCHOR_KINDS[j] === GUIDE_KIND.CENTER
              ? GUIDE_KIND.CENTER
              : GUIDE_KIND.EDGE,
          targetIds: [target.id],
        });
      }
    }
  }
  return guides;
};

/**
 * Cœur du magnétisme. Fonction pure : mêmes entrées → mêmes sorties, aucun
 * effet de bord, donc appelable aussi bien pendant le drag (aperçu) qu'au drop
 * (position réellement enregistrée) sans risque de divergence.
 *
 * @param {{x:number,y:number,width:number,height:number}} draggedBBox position courante non accrochée
 * @param {Array<{id:string,x:number,y:number,width:number,height:number}>} externalBBoxes cibles (box voisines + axes majeurs)
 * @param {number} threshold distance d'accrochage, en px document
 * @returns {{snappedX:number,snappedY:number,deltaX:number,deltaY:number,activeGuides:Array}}
 */
export const computeSnap = ({
  draggedBBox,
  externalBBoxes = [],
  threshold = SNAP_CONFIG.snapThreshold,
  maxGuidesPerAxis = SNAP_CONFIG.maxGuidesPerAxis,
  guideOverhang = SNAP_CONFIG.guideOverhang,
}) => {
  const targets = externalBBoxes.map(withAnchors);
  const bestX = bestAlignmentOnAxis({
    draggedAnchors: anchorsOnX(draggedBBox),
    targets,
    anchorsKey: 'ax',
    threshold,
  });
  const bestY = bestAlignmentOnAxis({
    draggedAnchors: anchorsOnY(draggedBBox),
    targets,
    anchorsKey: 'ay',
    threshold,
  });
  const deltaX = bestX ? bestX.delta : 0;
  const deltaY = bestY ? bestY.delta : 0;
  const snappedX = draggedBBox.x + deltaX;
  const snappedY = draggedBBox.y + deltaY;
  const snappedBBox = { ...draggedBBox, x: snappedX, y: snappedY };
  const activeGuides = [];
  if (bestX) {
    activeGuides.push(
      ...guidesOnAxis({
        axis: 'x',
        draggedBBox: snappedBBox,
        targets,
        maxGuides: maxGuidesPerAxis,
        overhang: guideOverhang,
      })
    );
  }
  if (bestY) {
    activeGuides.push(
      ...guidesOnAxis({
        axis: 'y',
        draggedBBox: snappedBBox,
        targets,
        maxGuides: maxGuidesPerAxis,
        overhang: guideOverhang,
      })
    );
  }
  return { snappedX, snappedY, deltaX, deltaY, activeGuides };
};

// Bord droit (axe X) / bas (axe Y) : le seul ancrage que déplacent les
// poignées de redimensionnement, le coin haut-gauche restant fixe.
const FAR_EDGE = [2];

/**
 * Magnétisme d'un redimensionnement : le bord tiré s'accroche aux bords et
 * centres des cibles (marges, centre de page, box voisines). Ni le bord fixe
 * ni le centre de la box, qui glisse à mi-vitesse, ne s'accrochent : seul ce
 * que la souris déplace colle.
 *
 * @param {{x:number,y:number,width:number,height:number}} bbox taille courante non accrochée
 * @param {{x:boolean,y:boolean}} axes axes redimensionnés (poignée droite → x, bas → y)
 * @returns {{width:number,height:number,deltaWidth:number,deltaHeight:number,activeGuides:Array}}
 */
export const computeResizeSnap = ({
  bbox,
  axes,
  externalBBoxes = [],
  threshold = SNAP_CONFIG.snapThreshold,
  maxGuidesPerAxis = SNAP_CONFIG.maxGuidesPerAxis,
  guideOverhang = SNAP_CONFIG.guideOverhang,
}) => {
  const targets = externalBBoxes.map(withAnchors);
  const bestOn = (axis) => axes?.[axis]
    ? bestAlignmentOnAxis({
      draggedAnchors: axis === 'x' ? anchorsOnX(bbox) : anchorsOnY(bbox),
      targets,
      anchorsKey: AXIS[axis].anchors,
      threshold,
      anchorIndices: FAR_EDGE,
    })
    : null;
  const bestX = bestOn('x');
  const bestY = bestOn('y');
  const deltaWidth = bestX ? bestX.delta : 0;
  const deltaHeight = bestY ? bestY.delta : 0;
  const snappedBBox = { ...bbox, width: bbox.width + deltaWidth, height: bbox.height + deltaHeight };
  const activeGuides = [];
  [['x', bestX], ['y', bestY]].forEach(([axis, best]) => {
    if (!best) return;
    activeGuides.push(
      ...guidesOnAxis({
        axis,
        draggedBBox: snappedBBox,
        targets,
        maxGuides: maxGuidesPerAxis,
        overhang: guideOverhang,
        anchorIndices: FAR_EDGE,
      })
    );
  });
  return {
    width: snappedBBox.width,
    height: snappedBBox.height,
    deltaWidth,
    deltaHeight,
    activeGuides,
  };
};

// Empreinte stable du jeu de guides actifs : sert de déclencheur haptique (un
// changement d'empreinte = un alignement gagné ou perdu). Arrondie au pixel
// pour ne pas vibrer sur du bruit de sous-pixel.
export const guidesSignature = (guides) =>
  guides
    .map(({ axis, position }) => `${axis}${Math.round(position)}`)
    .sort()
    .join('|');
